import { useEffect, useState, FormEvent, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import config from 'src/config';
import { useAuth } from 'src/util/authenticationProvider';
import AddIcon from '@mui/icons-material/Add';

type LeadList = {
  id: string;
  name: string;
  description?: string;
  targets?: LeadTarget[];
};

type LeadTarget = {
  id?: string;
  name: string;
  email: string;
  phone: string;
};

export default function LeadsLists() {
  const auth = useAuth();
  const [lists, setLists] = useState<LeadList[]>([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [availableLeads, setAvailableLeads] = useState<LeadTarget[]>([]);
  const [editDialogList, setEditDialogList] = useState<LeadList | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editSelectedLeadIds, setEditSelectedLeadIds] = useState<Set<string>>(new Set());
  const [editInitialLeadIds, setEditInitialLeadIds] = useState<Set<string>>(new Set());
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // use same auth header style as other components
  const headers = auth?.tokens ? { Authorization: `Bearer ${auth.tokens.accessToken}` } : undefined;

  useEffect(() => {
    fetchLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchLists() {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await axios.get(`${config.apiUrl}/api/leadlists/all`, { headers });
      // backend returns { status: 200, content: [...] }
      const payload = res?.data?.content ?? res?.data?.data ?? res?.data;
      const arr = Array.isArray(payload) ? payload : [];
      // map to ensure we have id/name/description fields
      setLists(arr.map((it: any) => ({
        id: it.id,
        name: it.name,
        description: it.description ?? '',
        targets: it.targets ?? []
      })));
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Failed to load lists');
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!name.trim()) {
      setError('List name is required');
      return;
    }

    setLoading(true);
    try {
      const payload = { name: name.trim(), description: description.trim() || undefined };

      await axios.post(`${config.apiUrl}/api/leadlists/create`, payload, { headers });
      setSuccess('List created');

      await fetchLists();
      resetForm();
      setCreateDialogOpen(false);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setName('');
    setDescription('');
    setError(null);
    setSuccess(null);
  }

  function startEdit(list: LeadList) {
    setEditDialogList(list);
    setEditName(list.name);
    setEditDescription(list.description ?? '');
    const existing = new Set<string>();
    (list.targets ?? []).forEach((t) => {
      const key = leadKey(t);
      if (key) existing.add(key);
    });
    setEditInitialLeadIds(new Set(existing));
    setEditSelectedLeadIds(new Set(existing));
    setSelectAllChecked(false);
    fetchAvailableLeads(existing);
  }

  async function handleDelete(id: string) {
    setDeleteTarget(id);
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      await axios.delete(`${config.apiUrl}/api/leadlists/delete/${encodeURIComponent(deleteTarget)}`, { headers });
      setSuccess('List deleted');
      await fetchLists();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Delete failed');
    } finally {
      setLoading(false);
      setDeleteTarget(null);
    }
  }

  function cancelDelete() {
    setDeleteTarget(null);
  }

  const fetchAvailableLeads = async (preselect?: Set<string>) => {
    try {
      const res = await axios.get(`${config.apiUrl}/api/leads/all`, { headers });
      const payload = res?.data?.content ?? res?.data ?? [];
      const arr = Array.isArray(payload) ? payload : [];
      const mapped = arr.map((lead: any) => ({
        id: lead.id ?? lead.email,
        name: lead.name,
        email: lead.email,
        phone: lead.phone
      }));
      setAvailableLeads(mapped);
      if (preselect) {
        const nextSelected = new Set<string>();
        mapped.forEach((lead) => {
          const key = leadKey(lead);
          if (preselect.has(key)) nextSelected.add(key);
        });
        setEditSelectedLeadIds(nextSelected);
        setSelectAllChecked(nextSelected.size > 0 && nextSelected.size === mapped.length);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Failed to load leads');
    }
  };

  const toggleLead = (leadId: string) => {
    setEditSelectedLeadIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId); else next.add(leadId);
      setSelectAllChecked(next.size === availableLeads.length && availableLeads.length > 0);
      return next;
    });
  };

  const handleSelectAll = () => {
    const allIds = new Set(availableLeads.map((l) => l.email ?? l.id));
    if (selectAllChecked) {
      setEditSelectedLeadIds(new Set());
      setSelectAllChecked(false);
    } else {
      const merged = new Set([...Array.from(editInitialLeadIds), ...Array.from(allIds)]);
      setEditSelectedLeadIds(merged);
      setSelectAllChecked(true);
    }
  };

  const selectionsUnchanged = useMemo(() => {
    if (editInitialLeadIds.size !== editSelectedLeadIds.size) return false;
    for (const id of editSelectedLeadIds) {
      if (!editInitialLeadIds.has(id)) return false;
    }
    return true;
  }, [editInitialLeadIds, editSelectedLeadIds]);

  const leadKey = (lead: { id?: string; email?: string; phone?: string }) => lead.email ?? lead.id ?? '';

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Lead Lists</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          New Lead List
        </Button>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      {loading && lists.length === 0 ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : lists.length === 0 ? (
        <Typography>No lists found.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lists.map((l) => (
              <TableRow key={l.id}>
                <TableCell>{l.name}</TableCell>
                <TableCell>{l.description}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => startEdit(l)} aria-label="edit">
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(l.id)} aria-label="delete">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={!!deleteTarget} onClose={cancelDelete}>
        <DialogTitle>Delete lead list?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete the selected lead list. Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete}>Cancel</Button>
          <Button color="error" onClick={confirmDelete} disabled={loading}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create Lead List</DialogTitle>
        <Box component="form" onSubmit={handleSubmit}>
          <DialogContent dividers>
            <Stack spacing={2}>
              <TextField
                label="List Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="List Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={loading} variant="contained">Create</Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={!!editDialogList} onClose={() => setEditDialogList(null)} fullWidth maxWidth="md">
        <DialogTitle>Edit Lead List</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" gap={2} mb={2}>
            <TextField
              label="List Name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="List Description"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              fullWidth
            />
          </Box>
          <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
            <Box>
              <Typography variant="subtitle2" mb={1}>Current Targets</Typography>
              {editSelectedLeadIds.size === 0 ? (
                <Typography variant="body2">No targets selected.</Typography>
              ) : (
                <List dense>
                {Array.from(editSelectedLeadIds).map((id) => {
                  const lead = availableLeads.find((l) => leadKey(l) === id) || (editDialogList?.targets ?? []).find((t) => leadKey(t) === id) || { name: id, email: id, phone: '' };
                  return (
                    <ListItem key={id} secondaryAction={
                      <IconButton edge="end" aria-label="remove" onClick={() => toggleLead(id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                      }>
                        <ListItemText primary={lead.name} secondary={`${lead.email} | ${lead.phone}`} />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Box>
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2">All Leads</Typography>
                <Button size="small" onClick={handleSelectAll}>{selectAllChecked ? 'Remove All' : 'Select All'}</Button>
              </Box>
              {availableLeads.length === 0 ? (
                <Typography variant="body2">No leads available.</Typography>
              ) : (
                <List dense>
                  {availableLeads.map((lead) => {
                    const leadId = leadKey(lead);
                    const checked = editSelectedLeadIds.has(leadId);
                    return (
                      <ListItem key={leadId} button onClick={() => toggleLead(leadId)}>
                        <ListItemIcon>
                          <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                        </ListItemIcon>
                        <ListItemText primary={lead.name} secondary={`${lead.email} | ${lead.phone}`} />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogList(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              loading ||
              !editDialogList ||
              !editName.trim() ||
              (selectionsUnchanged && editName === editDialogList.name && editDescription === (editDialogList.description ?? ''))
            }
            onClick={async () => {
              if (!editDialogList) return;
              setLoading(true);
              setError(null);
              setSuccess(null);
              try {
                const selectedTargets = Array.from(editSelectedLeadIds).map((id) => {
                  const source = availableLeads.find((l) => leadKey(l) === id) || (editDialogList.targets ?? []).find((t) => leadKey(t) === id);
                  return {
                    name: source?.name ?? '',
                    email: source?.email ?? '',
                    phone: source?.phone ?? ''
                  };
                });

                await axios.put(`${config.apiUrl}/api/leadlists/update/${encodeURIComponent(editDialogList.id)}`, {
                  name: editName.trim(),
                  description: editDescription.trim(),
                  targets: selectedTargets
                }, { headers });
                setSuccess('List updated');
                await fetchLists();
                setEditDialogList(null);
              } catch (err: any) {
                setError(err?.response?.data?.message || err.message || 'Update failed');
              } finally {
                setLoading(false);
              }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
